<template>
  <div class="app-container">
    <div>
      <el-select
        v-model="selectedDomainID"
        class="search-select"
        type="select"
        style="margin-bottom: 10px"
        :placeholder="$t('common.domain')"
        @change="handleDomainChange"
      >
        <el-option v-for="domain in domains" :key="domain.id" :label="domain.name" :value="domain.id" />
      </el-select>
      <date-picker
        ref="weekPicker"
        v-model="selectedWeek"
        type="week"
        :clearable="false"
        style="margin-bottom: 10px; margin-left: 10px"
        @input="handleDateChange"
      />
      <el-select
        ref="workshopPicker"
        v-model="selectedWorkshopID"
        class="search-select"
        type="select"
        style="margin-bottom: 10px; margin-left: 10px"
        :placeholder="$t('common.workshop')"
        @change="handleWorkshopChange"
      >
        <el-option
          v-for="workshop in workshops"
          :key="workshop.workshopID"
          :label="workshop.name"
          :value="workshop.workshopID"
        />
      </el-select>
      <el-input
        v-model="selectedSeries"
        :placeholder="$t('statusList.seriesPlaceholder')"
        style="margin-bottom: 10px; margin-left: 10px; width: 300px"
        :pattern="serialNumberPattern"
        @input="handleSeriesChange"
      />
      <div style="float:right;">
        <editor-button
          :icon-only="false"
          :disabled="!isDownloadPossible()"
          button-translation="common.downloadExcel"
          action="primary"
          @click="downloadOrderPickList()"
        />
      </div>
    </div>
    <div>
      <el-table :data="loadedData.items" border fit>
        <el-table-column :label="$t('series.seriesNumber')" width="120px">
          <template slot-scope="scope">
            <span>{{ scope.row['seriesNumber'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('series.seriesDescription')" width="200px">
          <template slot-scope="scope">
            <span>{{ scope.row['seriesDescription'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('series.seriesStatus')">
          <template slot-scope="scope">
            <span>{{ formatSeriesStatus(scope.row, scope.column, scope.row['status']) }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { ForceFileDownload } from '@/utils/general';
import DatePicker from '@/components/DSE/DatePicker';
import EditorButton from '@/components/crud/EditorButton';

const debounce = require('lodash.debounce');

export default {
  name: 'CreateOrderPickList',
  components: {
    DatePicker,
    EditorButton
  },
  data() {
    return {
      loading: false,
      loadedData: { items: [], total: 0 },
      productionPlanningID: 0,
      selectedDomainID: this.$store.getters['domain/getDefaultSelectValueDomainID'],
      selectedSeries: null,
      selectedWeek: null,
      selectedWorkshopID: null,
      workshops: [],
      serialNumberPattern: '^(\\d{1,3}|\\d{1,3}-\\d{1,3})(,\\d{1,3}|,\\d{1,3}-\\d{1,3})*$'
    };
  },
  computed: {
    domains() {
      return this.$store.getters.domains;
    }
  },
  created() {
    this.debouncedGetSeries = debounce(this.getSeries, 500);
  },
  methods: {
    async handleDomainChange() {
      await this.getWorkshops();
      this.getSeries();
    },
    async handleDateChange() {
      await this.getWorkshops();
      this.getSeries();
    },
    handleWorkshopChange() {
      this.getSeries();
    },
    handleSeriesChange() {
      if (this.selectedSeries === '') {
        this.selectedSeries = null;
      }
      this.debouncedGetSeries();
    },
    isDownloadPossible() {
      return (
        !this.loading &&
        this.productionPlanningID !== 0 &&
        (!this.selectedSeries || this.selectedSeries.match(this.serialNumberPattern))
      );
    },
    async getWorkshops() {
      if (this.selectedDomainID === null || this.selectedWeek === null) {
        return;
      }
      this.workshops = await this.$store.dispatch('productionPlanning/getWorkshops', {
        domainID: this.selectedDomainID,
        weekByDate: this.selectedWeek
      });
      this.resetDependantData();
    },
    getSeries() {
      if (this.selectedDomainID === null || this.selectedWeek === null || this.selectedWorkshopID == null) {
        return;
      }
      if (this.selectedSeries && !this.selectedSeries.match(this.serialNumberPattern)) {
        return;
      }
      this.loading = true;
      this.$store
        .dispatch('productionPlanning/getSeries', {
          domainID: this.selectedDomainID,
          weekByDate: this.selectedWeek,
          workshopID: this.selectedWorkshopID,
          serialNumbers: this.selectedSeries
        })
        .then(data => {
          if (data && data.length > 0) {
            this.productionPlanningID = data[0].productionPlanningID;
          }
          this.loadedData.items = data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetDependantData() {
      const isSelectedWorkshop = elem => elem.workshopID === this.selectedWorkshopID;
      if (!this.workshops || (this.selectedWorkshopID && this.workshops.findIndex(isSelectedWorkshop) < 0)) {
        this.selectedWorkshopID = null;
      }
      this.loadedData.items = [];
      this.loadedData.total = 0;
      this.productionPlanningID = 0;
    },
    downloadOrderPickList() {
      this.loading = true;
      this.$store
        .dispatch('orderPickList/downloadOrderPickList', {
          productionPlanningID: this.productionPlanningID,
          workshopID: this.selectedWorkshopID,
          serialNumbers: this.selectedSeries
        })
        .then(response => {
          let fileName = '';
          const disposition = response.request.getResponseHeader('Content-Disposition');
          // From SO: https://stackoverflow.com/a/40940790
          if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              fileName = matches[1].replace(/['"]/g, '');
            }
          } else {
            fileName =
              this.$refs.workshopPicker.selectedLabel +
              ' OrderPick' +
              this.$refs.weekPicker.getDisplayValue() +
              '.xlsx';
          }
          ForceFileDownload(response.data, fileName);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatSeriesStatus(row, column, cellValue) {
      const option =
        cellValue !== null &&
        this.$store.getters['option/loadedOptions']('seriesState').find(item => item.code === cellValue);
      return option && option.description;
    }
  }
};
</script>
